import type { Invitation, Program, ProgramReport } from '../api/usePrograms.ts';
import type { SporLedd } from '../innboks/Sporingslinje.tsx';
import { kortDato } from '../innboks/Rad.tsx';
import { programOppforing } from '../status/status.ts';
import { periode } from './periode.ts';

/**
 * Programmets tilstand som VISNING (R2.3, program-konsept v0.1): helten, sporingslinjen,
 * tallrekka og radens ene tall — utledet av kontraktens felter og ingenting annet.
 *
 * Datoene er dem `contracts/models/program.tsp` har: `createdAt`, `opensAt`, `closesAt` og
 * rapportens `frozenAt`. Det finnes INGEN `openedAt` og ingen `activatedAt`, så leddet
 * «Åpen» står uten dato og «Aktiv» bærer «fra opensAt» (avvik 4 i avvikslisten).
 */

/** Leddet under sporingslinjens NÅ-ledd (P2): løftet som sto i pillen til 2026-09-19. */
const NAA_LEDD: Record<string, string> = {
  aapen: 'dere kan invitere',
  avsluttes: 'oppfølginger går ut',
  avsluttet: 'rapport fryst',
};

/** Hvor langt programmet har kommet. Rekkefølgen er tilstandsmaskinen i domain-model.md. */
const STEG: Record<string, number> = { utkast: 0, aapen: 1, aktiv: 2, avsluttes: 3, avsluttet: 3 };

export interface Helt {
  /** Hva datoen er dato FOR — «Starter», «Slutter», «Sluttet», «Rapporten fryst», «Avbrutt». */
  etikett: string;
  tall: string;
  /** Leddet under: «til 24. okt», «om 2 dager», «endres ikke». */
  under: string;
}

function dager(iso: string, nå: Date): number {
  return Math.round((new Date(iso).getTime() - nå.getTime()) / 86_400_000);
}

/** «om 2 dager» · «i morgen» · «i dag» — sluttdatoen som ligger foran. */
function frem(iso: string, nå: Date): string {
  const d = dager(iso, nå);
  if (d <= 0) return 'i dag';
  if (d === 1) return 'i morgen';
  return `om ${d} dager`;
}

/** «3 dager siden» · «i går» · «i dag» — sluttdatoen som er passert. */
function tilbake(iso: string, nå: Date): string {
  const d = -dager(iso, nå);
  if (d <= 0) return 'i dag';
  if (d === 1) return 'i går';
  return `${d} dager siden`;
}

/**
 * Skjermens ENE helt (DD-67): datoen som tall. På et avsluttet program er det rapportens
 * konklusjon som er helten, og datoen står mindre — se `Programside`.
 */
export function helten(program: Program, rapport: ProgramReport | undefined, nå: Date): Helt {
  switch (program.status) {
    case 'aktiv':
      return { etikett: 'Slutter', tall: kortDato(program.closesAt), under: frem(program.closesAt, nå) };
    case 'avsluttes':
      return { etikett: 'Sluttet', tall: kortDato(program.closesAt), under: tilbake(program.closesAt, nå) };
    case 'avsluttet':
      return rapport
        ? { etikett: 'Rapporten fryst', tall: kortDato(rapport.frozenAt), under: 'endres ikke' }
        : { etikett: 'Sluttet', tall: kortDato(program.closesAt), under: tilbake(program.closesAt, nå) };
    case 'avbrutt':
      return { etikett: 'Avbrutt', tall: kortDato(program.updatedAt), under: 'lukket for alle' };
    default:
      return { etikett: 'Starter', tall: kortDato(program.opensAt), under: `til ${kortDato(program.closesAt)}` };
  }
}

/**
 * Sporingslinjen Utkast — Åpen — Aktiv — Avsluttet. Siste ledd bærer sluttdatoen som tall
 * bare når helten ikke alt er den datoen (avvik 18).
 *
 * Avbrutt: kontrakten sier ikke HVILKEN tilstand programmet ble avbrutt fra — det finnes
 * ingen `cancelledFrom` — så linjen stopper på leddet vi kan bevise: utkastet ble laget, og
 * så ble programmet avbrutt. Wireframens `avbruttPaa` er mockdata, ikke et felt.
 */
export function sporLedd(program: Program): SporLedd[] {
  if (program.status === 'avbrutt') {
    return [
      { key: 'utkast', navn: 'Utkast', under: kortDato(program.createdAt), tilstand: 'ferdig' },
      { key: 'avbrutt', navn: programOppforing('avbrutt').etikett, under: kortDato(program.updatedAt), tilstand: 'naa', ikon: programOppforing('avbrutt').ikon, tone: 'avbrutt' },
    ];
  }
  const nådd = STEG[program.status] ?? 0;
  const aktivUnder = nådd > 2 ? `${kortDato(program.opensAt)} – ${kortDato(program.closesAt)}` : nådd === 2 ? `fra ${kortDato(program.opensAt)}` : 'når dere bekrefter flagget';
  const rå: Array<{ key: string; navn: string; under?: string }> = [
    { key: 'utkast', navn: 'Utkast', under: kortDato(program.createdAt) },
    { key: 'aapen', navn: 'Åpen' },
    { key: 'aktiv', navn: 'Aktiv', under: aktivUnder },
    { key: 'slutt', navn: program.status === 'avsluttet' ? 'Avsluttet' : 'Avsluttes' },
  ];
  return rå.map((l, i): SporLedd => {
    if (i < nådd) return { ...l, tilstand: 'ferdig' };
    if (i > nådd) return { key: l.key, navn: l.navn, ...(l.key === 'aktiv' ? { under: aktivUnder } : {}), tilstand: 'kommende' };
    const o = programOppforing(program.status);
    return {
      key: l.key,
      navn: l.navn,
      ...(NAA_LEDD[program.status] ? { under: NAA_LEDD[program.status]! } : l.under ? { under: l.under } : {}),
      // Sluttdatoen som tall på siste ledd: bare i Åpen, der helten er STARTdatoen.
      ...(l.key === 'slutt' && program.status === 'aapen' ? { tall: kortDato(program.closesAt) } : {}),
      tilstand: 'naa',
      ikon: o.ikon,
      ...(program.status === 'avsluttes' ? { tone: 'varsel' as const } : {}),
    };
  });
}

export interface Tall {
  key: string;
  navn: string;
  verdi: string;
  /** Egen testid når tallet har en regel knyttet til seg — prosenten over terskelen. */
  testid?: string;
  /** Alt utenom det ene tallet som teller NÅ er dempet (DD-67: én helt). */
  dempet?: boolean;
  /** «± 9» ved siden av prosenten. */
  tillegg?: string;
}

/**
 * «Med» er akseptert MINUS de leietakeren ikke har bekreftet flagget for: en deltaker som
 * har sagt ja men ikke fått flagget er ikke med ennå (DD-36). Utledet, aldri lagret.
 */
export function medTall(program: Program): number {
  return Math.max(0, program.participantCount - program.invitedNotEnabled);
}

/**
 * Tre tall uten prosa (F9). Nuller vises ikke, med ett unntak: det FØRSTE tallet står
 * uansett — en tom flate der tallet skulle vært leses som en feil (avvik 14).
 */
export function tallrekke(program: Program, invitasjoner: Invitation[]): Tall[] {
  const rader: Tall[] =
    program.status === 'aapen' || program.status === 'avbrutt'
      ? [
          { key: 'akseptert', navn: 'Akseptert', verdi: String(program.participantCount) },
          { key: 'ikke-paa', navn: 'Ikke slått på', verdi: String(program.invitedNotEnabled), dempet: true },
          { key: 'ikke-svart', navn: 'Ikke svart', verdi: String(invitasjoner.filter((i) => i.status === 'sendt').length), dempet: true },
        ]
      : [
          { key: 'med', navn: 'Med', verdi: String(medTall(program)) },
          { key: 'ikke-paa', navn: 'Ikke slått på', verdi: String(program.invitedNotEnabled), dempet: true },
          { key: 'trukket', navn: 'Trukket', verdi: String(program.withdrawnUnconfirmed), dempet: true },
        ];
  return rader.filter((r, i) => i === 0 || r.verdi !== '0');
}

/**
 * Radens ENE tall (DD-67) og den ene meta-linjen.
 *
 * Konseptet viser også «5 invitert» på et åpent program og konklusjonen «Delvis» på et
 * avsluttet. Begge krever ett ekstra kall PER PROGRAM (`/invitations`, `/report`) —
 * lista henter bare `/programs` — så de står ikke her. Kontraktens tabell i konseptet
 * sier at R2.3 avgjør det, og svaret er: for dyrt for en liste (N + 1).
 */
export interface Radtekst {
  /** Tallet, halvfett. Utkast har ingen telling — der står perioden som radens tall. */
  verdi?: string;
  ord: string;
  /** «⏱ rapport» i warning når rapporten venter på leietakeren — som «⚠ dato» i innboksen. */
  varsel?: boolean;
  meta: string[];
}

export function radtekst(program: Program): Radtekst {
  const r = radtekstUtenHypotese(program);
  // H3.4: programmet laget fra en hypotese bærer nummeret i meta-linjen (konseptets skjerm 8 og 9).
  return program.fromHypothesis ? { ...r, meta: [...r.meta, `fra H-${program.fromHypothesis.number}`] } : r;
}

function radtekstUtenHypotese(program: Program): Radtekst {
  const p = periode(program.opensAt, program.closesAt);
  const status = programOppforing(program.status).etikett;
  const med = medTall(program);
  switch (program.status) {
    case 'utkast':
      return { ord: p, meta: [status, program.participantCount > 0 ? `${program.participantCount} akseptert` : 'ingen med ennå'] };
    case 'aapen':
      return { verdi: String(program.participantCount), ord: 'akseptert', meta: [status, p] };
    case 'avsluttes':
      return { ord: 'rapport', varsel: true, meta: [status, `sluttet ${kortDato(program.closesAt)}`, `${med} med`] };
    default:
      return { verdi: String(med), ord: 'med', meta: [status, p, ...(program.invitedNotEnabled > 0 ? [`${program.invitedNotEnabled} ikke slått på`] : [])] };
  }
}
